/** Misc display formatting — dates and contact names. All data-driven. */

import type { Quote, PublicQuote } from "@/types";

/** Xano returns ISO strings or epoch millis depending on field; handle both. */
export function formatDate(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  const d = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function formatDateShort(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  const d = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/** Numeric `dd/MM/yyyy` (for dense dashboard tables). 0/unset → "—". */
export function formatDateDMY(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "" || value === 0) return "—";
  const d = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** `yyyy-mm-dd` for an <input type="date">, from an ISO string / epoch / null.
 *  Uses local date parts (UK is UTC/UTC+1) so a UTC-midnight timestamp doesn't
 *  shift the displayed day. Returns "" when unset. */
export function toDateInputValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "" || value === 0) return "";
  const d = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Coarse "time ago" from an ISO string / epoch (e.g. "3 hours ago", "just now").
 *  For freshness hints where the exact timestamp matters less than the lag.
 *  Falls back to the short date for anything older than ~a month, or "—" if unset. */
export function formatRelative(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  const d = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) return formatDateShort(value); // future — show the date rather than "in …"
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return formatDateShort(value);
}

/** Date + time in Europe/London (for consultation slots). 0/unset → "—". */
export function formatDateTime(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "" || value === 0) return "—";
  const d = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Display name from contact/company fields, never hard-coded. */
export function contactName(
  q: Pick<Quote, "contact_first_name" | "contact_last_name" | "company_name"> | PublicQuote,
): string {
  const full = [q.contact_first_name, q.contact_last_name].filter(Boolean).join(" ").trim();
  if (q.company_name && full) return `${full}, ${q.company_name}`;
  return q.company_name || full || "—";
}

/** Single-line site address from the structured fields. */
export function siteAddress(
  q: Pick<
    PublicQuote,
    | "site_first_line_of_address"
    | "site_address_line_1"
    | "site_town"
    | "site_county"
    | "site_postcode"
  >,
): string {
  const parts = [
    q.site_first_line_of_address || q.site_address_line_1,
    q.site_town,
    q.site_county,
    q.site_postcode,
  ].filter(Boolean);
  return parts.join(", ");
}
